import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, UserPlus } from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import { getInvoiceDefaults } from "@/utils/companyProfile";

export function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

export function InstallmentsEditor({ total, value, onChange }) {
  const rows = value ?? [];

  const setRow = (i, field, v) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [field]: v } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, { id: Date.now(), due_date: "", amount: 0 }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  const splitEqually = () => {
    const n = Math.max(rows.length, 2);
    const cents = Math.round((total ?? 0) * 100);
    const base = Math.floor(cents / n);
    const out = [];
    for (let i = 0; i < n; i++) {
      const amount = (i === n - 1 ? cents - base * (n - 1) : base) / 100;
      out.push({ id: Date.now() + i, due_date: rows[i]?.due_date ?? "", amount });
    }
    onChange(out);
  };

  const sum = rows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
  const mismatch = Math.abs(sum - (total ?? 0)) > 0.01;

  return (
    <div className="space-y-2 border rounded-md p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Calendario de plazos</span>
        <div className="flex gap-2">
          <button type="button" className="text-xs underline" onClick={splitEqually}>
            Repartir en partes iguales
          </button>
          <button type="button" className="text-xs underline" onClick={addRow}>
            + Añadir plazo
          </button>
        </div>
      </div>
      {rows.map((r, i) => (
        <div key={r.id ?? i} className="flex items-center gap-2">
          <span className="text-xs w-14">Plazo {i + 1}</span>
          <input
            type="date"
            className="border rounded px-2 py-1 text-sm"
            value={r.due_date ?? ""}
            onChange={(e) => setRow(i, "due_date", e.target.value)}
          />
          <input
            type="number"
            step="0.01"
            className="border rounded px-2 py-1 text-sm w-28"
            value={r.amount ?? 0}
            onChange={(e) => setRow(i, "amount", parseFloat(e.target.value) || 0)}
          />
          <span className="text-xs">EUR</span>
          <button type="button" className="text-xs text-red-500" onClick={() => removeRow(i)}>
            ✕
          </button>
        </div>
      ))}
      <div className={`text-xs ${mismatch ? "text-red-600" : "text-green-600"}`}>
        Suma de plazos: {sum.toFixed(2)} EUR / Total: {(total ?? 0).toFixed(2)} EUR
        {mismatch ? " — no cuadra" : " ✓"}
      </div>
    </div>
  );
}

export default function InvoiceForm({ invoice, onSave, onCancel, clients, onClientCreated }) {
  const [formData, setFormData] = useState({
    series: invoice?.series ?? "2026",
    invoice_number: "",
    client_id: invoice?.client_id || null,
    service_id: invoice?.service_id || null,
    client_name: invoice?.client_name || "",
    client_nif: invoice?.client_nif || "",
    client_address: invoice?.client_address || "",
    client_city: invoice?.client_city || "",
    client_postal_code: invoice?.client_postal_code || "",
    client_country: invoice?.client_country ?? "España",
    client_email: invoice?.client_email || "",
    invoice_date: invoice?.invoice_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    due_date: invoice?.due_date?.slice(0, 10) || "",
    concept: invoice?.concept || "",
    period_text: invoice?.period_text || "",
    base_amount: invoice?.base_amount || "",
    vat_rate: invoice?.vat_rate ?? 21,
    irpf_rate: invoice?.irpf_rate ?? 0,
  });

  const [paymentMethod, setPaymentMethod] = useState(invoice?.payment_method ?? "transfer");
  const [installments, setInstallments] = useState(invoice?.payment_schedule ?? []);

  const [isRecurring, setIsRecurring] = useState(invoice?.is_recurring ?? false);
  const [recurrenceEnd, setRecurrenceEnd] = useState(invoice?.recurrence_end?.slice(0, 10) ?? "");

  const [selectedClientId, setSelectedClientId] = useState(invoice?.client_id || "");
  const [services, setServices] = useState([]);

  // Hallazgo 2: alta de cliente al vuelo desde el propio selector.
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "", nif: "", email: "", address: "", city: "", postal_code: "",
  });
  const [creatingClient, setCreatingClient] = useState(false);

  // Integridad contable (roadmap §1.2 bloque 2): con cobros conciliados,
  // compensada o sellada, los campos con efecto contable quedan bloqueados.
  // El camino para corregir importes es la rectificativa (factura negativa).
  const sealed = Boolean(invoice?.filed_period);
  const hasCollections = Boolean(invoice && (invoice.amount_collected || 0) > 0);
  const compensated = invoice?.payment_status === "compensated";
  const lockedFiscal = sealed || hasCollections || compensated;

  // Errores de campos obligatorios: {campo: true}. Se rellenan en submit y se limpian al corregir cada campo.
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Factura NUEVA: los defaults fiscales salen del perfil de empresa.
  // Al editar (invoice existe) se conservan los valores de la factura.
  useEffect(() => {
    if (invoice) return;
    let cancelled = false;
    getInvoiceDefaults().then((d) => {
      if (cancelled) return;
      setFormData((prev) =>
        // Solo si el usuario no ha tocado aún IVA/IRPF (siguen en los literales iniciales)
        prev.vat_rate === 21 && prev.irpf_rate === 0
          ? { ...prev, vat_rate: d.vat_rate, irpf_rate: d.irpf_rate }
          : prev
      );
    });
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  useEffect(() => {
    if (!selectedClientId || selectedClientId === "__manual__") {
      setServices([]);
      return;
    }
    const fetchServices = async () => {
      try {
        const response = await apiFetch(`${API}/crm/services?client_id=${selectedClientId}`);
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, [selectedClientId]);

  const applyClientToForm = (client) => {
    setFormData((prev) => ({
      ...prev,
      client_id: client.client_id,
      service_id: null,
      client_name: client.name || "",
      client_nif: client.nif || "",
      client_address: client.address || "",
      client_city: client.city || "",
      client_postal_code: client.postal_code || "",
      client_email: client.email || "",
    }));
    clearFieldError("client_name");
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) return;
    setCreatingClient(true);
    try {
      const response = await apiFetch(`${API}/crm/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newClient.name.trim(),
          nif: newClient.nif.trim() || null,
          email: newClient.email.trim() || null,
          address: newClient.address.trim() || null,
          city: newClient.city.trim() || null,
          postal_code: newClient.postal_code.trim() || null,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo crear el cliente");
      }
      const doc = await response.json();
      onClientCreated?.(doc);
      setSelectedClientId(doc.client_id);
      applyClientToForm(doc);
      setShowNewClient(false);
      setNewClient({ name: "", nif: "", email: "", address: "", city: "", postal_code: "" });
      toast.success(`Cliente "${doc.name}" creado y seleccionado`);
    } catch (error) {
      toast.error(error.message || "No se pudo crear el cliente");
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSelectClient = (clientId) => {
    if (clientId === "__create__") {
      // No cambia la selección: abre el alta al vuelo (hallazgo 2).
      setShowNewClient(true);
      return;
    }
    setSelectedClientId(clientId);
    if (clientId === "__manual__") {
      setFormData((prev) => ({ ...prev, client_id: null, service_id: null }));
      return;
    }
    const client = clients.find((c) => c.client_id === clientId);
    if (client) {
      setFormData((prev) => ({
        ...prev,
        client_id: client.client_id,
        service_id: null,
        client_name: client.name || "",
        client_nif: client.nif || "",
        client_address: client.address || "",
        client_city: client.city || "",
        client_postal_code: client.postal_code || "",
        client_email: client.email || "",
      }));
      clearFieldError("client_name");
    }
  };

  const handleSelectService = (serviceId) => {
    if (serviceId === "__none__") {
      setFormData((prev) => ({ ...prev, service_id: null }));
      return;
    }
    const svc = services.find((s) => s.service_id === serviceId);
    if (svc) {
      setFormData((prev) => ({
        ...prev,
        service_id: svc.service_id,
        concept: prev.concept || svc.service_name || "",
      }));
    }
  };

  const vatAmount = (formData.base_amount || 0) * (formData.vat_rate / 100);
  const irpfAmount = (formData.base_amount || 0) * (formData.irpf_rate / 100);
  const totalAmount = (formData.base_amount || 0) + vatAmount - irpfAmount;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Campos obligatorios que faltan: se marcan en el formulario y se enumeran en el toast.
    const missing = {};
    if (!formData.client_name) missing.client_name = true;
    if (!formData.concept) missing.concept = true;
    if (!formData.base_amount) missing.base_amount = true;
    if (paymentMethod === "transfer" && !formData.due_date) missing.due_date = true;
    if (isRecurring && !recurrenceEnd) missing.recurrenceEnd = true;
    if (Object.keys(missing).length > 0) {
      const labels = {
        client_name: "Cliente",
        concept: "Concepto",
        base_amount: "Base Imponible",
        due_date: "Vencimiento",
        recurrenceEnd: "Generar hasta",
      };
      setFieldErrors(missing);
      toast.error(`Faltan campos obligatorios: ${Object.keys(missing).map((k) => labels[k]).join(", ")}`);
      // Scroll al primer campo con error (el form vive en un diálogo con overflow)
      setTimeout(() => {
        document.querySelector('[data-field-error="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setFieldErrors({});
    // Validate installments sum if payment method is installments
    if (paymentMethod === "installments") {
      const liveTotal = Math.round(
        ((formData.base_amount || 0) * (1 + (formData.vat_rate ?? 21) / 100) -
          (formData.base_amount || 0) * ((formData.irpf_rate ?? 0) / 100)) * 100
      ) / 100;
      const sum = installments.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
      if (Math.abs(sum - liveTotal) > 0.01) {
        toast.error(`La suma de los plazos (${sum.toFixed(2)} €) no coincide con el total (${liveTotal.toFixed(2)} €)`);
        return;
      }
    }
    // Strip empty invoice_number so backend auto-increments. Send int if filled.
    const payload = { ...formData };
    if (payload.invoice_number === "" || payload.invoice_number === null || payload.invoice_number === undefined) {
      delete payload.invoice_number;
    } else {
      const parsed = parseInt(payload.invoice_number, 10);
      if (Number.isNaN(parsed) || parsed < 1) {
        toast.error("El numero de factura debe ser un entero >= 1");
        return;
      }
      payload.invoice_number = parsed;
    }
    payload.payment_method = paymentMethod;
    payload.payment_schedule = paymentMethod === "installments" ? installments : null;
    payload.is_recurring = isRecurring;
    payload.recurrence_end = isRecurring ? recurrenceEnd : null;
    payload.recurrence_frequency = "monthly";
    if (lockedFiscal) {
      // Enviar SOLO los campos sin efecto contable: el backend rechaza la
      // petición entera si incluye cualquier campo bloqueado, aunque no cambie.
      const allowed = [
        "client_id", "service_id", "client_address", "client_city",
        "client_postal_code", "client_email", "client_country",
        "concept", "period_text", "due_date", "payment_method",
        "payment_schedule", "is_recurring", "recurrence_end", "recurrence_frequency",
      ];
      if (!sealed) allowed.push("client_name", "client_nif");
      const filtered = {};
      for (const k of allowed) {
        if (k in payload) filtered[k] = payload[k];
      }
      onSave(filtered);
      return;
    }
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      {sealed && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="sealed-warning">
          <p className="font-medium">Factura incluida en una declaración ya presentada ({invoice.filed_period})</p>
          <p className="mt-1">
            Modificar sus datos contables puede requerir una declaración complementaria
            o sustitutiva ante la AEAT; consúltalo con tu asesor si lo tienes. Los campos
            con efecto contable están bloqueados: para corregir importes, crea una factura
            rectificativa (importe negativo). Si necesitas otra cosa, primero deshaz el
            sellado del periodo (Fiscal → Deshacer presentado).
          </p>
        </div>
      )}
      {!sealed && lockedFiscal && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="collected-warning">
          <p className="font-medium">
            {compensated
              ? "Factura compensada con otras"
              : `Factura con cobros conciliados (${formatCurrency(invoice.amount_collected || 0)})`}
          </p>
          <p className="mt-1">
            Los importes y datos fiscales están bloqueados. Para corregirlos, el camino
            es una factura rectificativa (una factura nueva de importe negativo que anule
            o ajuste esta). Si el cobro conciliado no corresponde a esta factura, deshaz
            la conciliación en Conciliación → Historial.
          </p>
        </div>
      )}
      {/* Client selector */}
      {!invoice && (
        <div>
          <Label className="text-sm font-semibold">Seleccionar Cliente</Label>
          <Select value={selectedClientId} onValueChange={handleSelectClient}>
            <SelectTrigger data-testid="select-client-dropdown" className="mt-1">
              <SelectValue placeholder="Selecciona un cliente del CRM..." />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>
                  {c.name} {c.nif ? `(${c.nif})` : ""}
                </SelectItem>
              ))}
              <SelectItem value="__create__">
                <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                  <Plus className="w-3.5 h-3.5" /> Crear cliente nuevo
                </span>
              </SelectItem>
              <SelectItem value="__manual__">
                <span className="flex items-center gap-1.5 text-slate-500">
                  <UserPlus className="w-3.5 h-3.5" /> Introducir manualmente
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          {clients.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No hay clientes en el CRM todavía: usa «Crear cliente nuevo» para darlo
              de alta sin salir de aquí.
            </p>
          )}

          {/* Alta de cliente al vuelo (hallazgo 2): se guarda en el CRM y se
              selecciona automáticamente en la factura. */}
          <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
            <DialogContent className="max-w-md" data-testid="new-client-inline-dialog">
              <DialogHeader>
                <DialogTitle>Crear cliente nuevo</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    data-testid="inline-client-name"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Nombre o razón social"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>NIF/CIF</Label>
                    <Input
                      data-testid="inline-client-nif"
                      value={newClient.nif}
                      onChange={(e) => setNewClient({ ...newClient, nif: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      data-testid="inline-client-email"
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Dirección</Label>
                  <Input
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Ciudad</Label>
                    <Input
                      value={newClient.city}
                      onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Código postal</Label>
                    <Input
                      value={newClient.postal_code}
                      onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  El cliente se guarda en el CRM; podrás completar el resto de datos
                  en la sección Clientes.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewClient(false)}>
                  Cancelar
                </Button>
                <Button
                  data-testid="inline-client-save"
                  onClick={handleCreateClient}
                  disabled={!newClient.name.trim() || creatingClient}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {creatingClient ? "Creando..." : "Crear y seleccionar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {services.length > 0 && (
        <div>
          <Label className="text-sm font-semibold">Servicio</Label>
          <Select
            value={formData.service_id || "__none__"}
            onValueChange={handleSelectService}
          >
            <SelectTrigger data-testid="select-service-dropdown" className="mt-1">
              <SelectValue placeholder="Selecciona un servicio..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin servicio especifico</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.service_id} value={s.service_id}>
                  {s.service_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Serie</Label>
          <Input
            value={formData.series}
            onChange={(e) => setFormData({ ...formData, series: e.target.value })}
            placeholder="2026, R, C, vacio..."
            data-testid="invoice-series-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">Deja vacio para facturas sin prefijo (ej. historico 2025).</p>
        </div>
        <div>
          <Label>Numero (opcional)</Label>
          <Input
            type="number"
            min="1"
            value={formData.invoice_number}
            onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
            placeholder="auto"
            data-testid="invoice-number-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">Solo para carga historica. Vacio = siguiente automatico.</p>
        </div>
        <div>
          <Label>Fecha *</Label>
          <Input
            type="date"
            value={formData.invoice_date}
            onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
            data-testid="emitted-date-input"
            disabled={lockedFiscal}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div data-field-error={fieldErrors.client_name ? "true" : undefined}>
          <Label>Cliente *</Label>
          <Input
            value={formData.client_name}
            onChange={(e) => {
              setFormData({ ...formData, client_name: e.target.value });
              clearFieldError("client_name");
            }}
            placeholder="Nombre del cliente"
            data-testid="emitted-client-input"
            disabled={sealed}
            className={fieldErrors.client_name ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.client_name && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
        <div>
          <Label>NIF Cliente</Label>
          <Input
            value={formData.client_nif}
            onChange={(e) => setFormData({ ...formData, client_nif: e.target.value })}
            placeholder="B12345678"
            data-testid="emitted-nif-input"
            disabled={sealed}
          />
        </div>
      </div>

      <div>
        <Label>Direccion Cliente</Label>
        <Input
          value={formData.client_address}
          onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
          placeholder="Calle y numero"
          data-testid="emitted-address-input"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Ciudad</Label>
          <Input
            value={formData.client_city}
            onChange={(e) => setFormData({ ...formData, client_city: e.target.value })}
            placeholder="Ciudad"
            data-testid="emitted-city-input"
          />
        </div>
        <div>
          <Label>Codigo Postal</Label>
          <Input
            value={formData.client_postal_code}
            onChange={(e) => setFormData({ ...formData, client_postal_code: e.target.value })}
            placeholder="28001"
            data-testid="emitted-postal-input"
          />
        </div>
        <div>
          <Label>País del cliente</Label>
          <Input
            value={formData.client_country}
            onChange={(e) => setFormData({ ...formData, client_country: e.target.value })}
            placeholder="España"
            data-testid="emitted-client-country-input"
          />
        </div>
      </div>

      <div>
        <Label>Email Cliente</Label>
        <Input
          type="email"
          value={formData.client_email}
          onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
          placeholder="cliente@empresa.com"
          data-testid="emitted-email-input"
        />
      </div>

      <div data-field-error={fieldErrors.concept ? "true" : undefined}>
        <Label>Concepto *</Label>
        <Textarea
          value={formData.concept}
          onChange={(e) => {
            setFormData({ ...formData, concept: e.target.value });
            clearFieldError("concept");
          }}
          placeholder="Descripcion del servicio o producto"
          rows={3}
          data-testid="emitted-concept-input"
          className={fieldErrors.concept ? "border-red-500 focus-visible:ring-red-500" : undefined}
        />
        {fieldErrors.concept && (
          <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
        )}
      </div>

      {/* Hallazgo 3: rótulo y visibilidad configurables en Ajustes → Documentos.
          Oculto ⇒ el PDF tampoco imprime la línea (ni el fallback automático). */}
      <div>
        {/* Nombre genérico a petición de Jorge (10-07): vale para servicios
            por periodos y para producto, sin configuración en Ajustes. */}
        <Label>Detalle del producto o servicio (texto bajo el concepto)</Label>
        <Input
          value={formData.period_text}
          onChange={(e) => setFormData({ ...formData, period_text: e.target.value })}
          placeholder="Ej: correspondiente a un mes de servicio"
          data-testid="emitted-period-input"
        />
        <p className="text-xs text-slate-400 mt-1">Si lo dejas vacio, el PDF mostrara automaticamente "Servicio prestado del [hace 30 dias] al [fecha factura]".</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div data-field-error={fieldErrors.base_amount ? "true" : undefined}>
          <Label>Base Imponible *</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.base_amount}
            onChange={(e) => {
              setFormData({ ...formData, base_amount: parseFloat(e.target.value) || "" });
              clearFieldError("base_amount");
            }}
            placeholder="0.00"
            data-testid="emitted-base-input"
            disabled={lockedFiscal}
            className={fieldErrors.base_amount ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.base_amount && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
        <div>
          <Label>IVA (%)</Label>
          <Select
            value={String(formData.vat_rate)}
            disabled={lockedFiscal}
            onValueChange={(value) => setFormData({ ...formData, vat_rate: parseFloat(value) })}
          >
            <SelectTrigger data-testid="emitted-vat-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="4">4%</SelectItem>
              <SelectItem value="10">10%</SelectItem>
              <SelectItem value="21">21%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>IRPF (%)</Label>
          <Select
            value={String(formData.irpf_rate)}
            disabled={lockedFiscal}
            onValueChange={(value) => setFormData({ ...formData, irpf_rate: parseFloat(value) })}
          >
            <SelectTrigger data-testid="emitted-irpf-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="7">7%</SelectItem>
              <SelectItem value="15">15%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Método de pago</Label>
        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
          <SelectTrigger data-testid="emitted-payment-method-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="transfer">Transferencia</SelectItem>
            <SelectItem value="installments">Pago fraccionado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {paymentMethod !== "installments" && (
        <div data-field-error={fieldErrors.due_date ? "true" : undefined}>
          <Label>Vencimiento <span className="text-red-500">*</span></Label>
          <Input
            type="date"
            value={formData.due_date}
            onChange={(e) => {
              setFormData({ ...formData, due_date: e.target.value });
              clearFieldError("due_date");
            }}
            data-testid="emitted-due-input"
            className={fieldErrors.due_date ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.due_date && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
      )}

      {paymentMethod === "installments" && (
        <InstallmentsEditor
          total={Math.round(
            ((formData.base_amount || 0) * (1 + (formData.vat_rate ?? 21) / 100) -
              (formData.base_amount || 0) * ((formData.irpf_rate ?? 0) / 100)) * 100
          ) / 100}
          value={installments}
          onChange={setInstallments}
        />
      )}

      {/* Factura recurrente */}
      <div className="rounded-lg border border-slate-200 p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="invoice-recurring-checkbox"
            checked={isRecurring}
            onCheckedChange={(checked) => setIsRecurring(checked === true)}
            data-testid="emitted-recurring-checkbox"
          />
          <Label htmlFor="invoice-recurring-checkbox" className="cursor-pointer">
            Factura recurrente (se genera sola cada mes)
          </Label>
        </div>
        {isRecurring && (
          <div data-field-error={fieldErrors.recurrenceEnd ? "true" : undefined}>
            <Label>Generar hasta <span className="text-red-500">*</span></Label>
            <Input
              type="date"
              value={recurrenceEnd}
              onChange={(e) => {
                setRecurrenceEnd(e.target.value);
                clearFieldError("recurrenceEnd");
              }}
              data-testid="emitted-recurrence-end-input"
              className={fieldErrors.recurrenceEnd ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.recurrenceEnd && (
              <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Cada mes se generará una copia automáticamente hasta esta fecha. No se envía al cliente: solo se crea y se te avisa por email.
            </p>
          </div>
        )}
      </div>

      {/* Totals Preview */}
      <div className="bg-slate-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Base Imponible</span>
          <span className="font-mono">{formatCurrency(formData.base_amount || 0)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">IVA ({formData.vat_rate}%)</span>
          <span className="font-mono">{formatCurrency(vatAmount)}</span>
        </div>
        {formData.irpf_rate > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Retencion IRPF ({formData.irpf_rate}%)</span>
            <span className="font-mono text-rose-600">-{formatCurrency(irpfAmount)}</span>
          </div>
        )}
        <div className="flex justify-between font-medium pt-2 border-t border-slate-200">
          <span>Total</span>
          <span className="font-mono text-lg">{formatCurrency(totalAmount)}</span>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" data-testid="save-emitted-btn">
          Guardar
        </Button>
      </DialogFooter>
    </form>
  );
}
